import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface Product {
  id: string;
  name: string;
  price: number;
  originalPrice?: number;
  images: string[];
  description: string;
  features: string[];
  category: string;
  subcategory?: string;
  rating: number;
  reviews: number;
  stock: number;
  tags: string[];
  dimensions?: {
    length: number;
    width: number;
    height: number;
  };
  material?: string;
  warranty?: string;
  isFeatured: boolean;
  isNewArrival: boolean;
  isBestSeller: boolean;
  createdAt: string;
  commentCount?: number;
  deliverablePincodes?: string[];
}

interface ProductsState {
  items: Product[];
  filteredItems: Product[];
  categories: string[];
  isLoading: boolean;
  error: string | null;
  filters: {
    category: string;
    priceRange: [number, number];
    rating: number;
    sortBy: string;
  };
}

const initialState: ProductsState = {
  items: [],
  filteredItems: [],
  categories: [],
  isLoading: false,
  error: null,
  filters: {
    category: '',
    priceRange: [0, 100000],
    rating: 0,
    sortBy: 'name',
  },
};

const productsSlice = createSlice({
  name: 'products',
  initialState,
  reducers: {
    setProducts: (state, action: PayloadAction<Product[]>) => {
      state.items = action.payload;
      state.filteredItems = action.payload;
      state.categories = [...new Set(action.payload.map(product => product.category))];
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setFilters: (state, action: PayloadAction<Partial<ProductsState['filters']>>) => {
      state.filters = { ...state.filters, ...action.payload };
      
      // Apply filters
      let filtered = [...state.items];
      
      if (state.filters.category) {
        filtered = filtered.filter(product => product.category === state.filters.category);
      }
      
      filtered = filtered.filter(
        product => product.price >= state.filters.priceRange[0] && 
                  product.price <= state.filters.priceRange[1]
      );
      
      if (state.filters.rating > 0) {
        filtered = filtered.filter(product => product.rating >= state.filters.rating);
      }
      
      // Apply sorting
      switch (state.filters.sortBy) {
        case 'price-low':
          filtered.sort((a, b) => a.price - b.price);
          break;
        case 'price-high':
          filtered.sort((a, b) => b.price - a.price);
          break;
        case 'rating':
          filtered.sort((a, b) => b.rating - a.rating);
          break;
        case 'newest':
          filtered.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
          break;
        default:
          filtered.sort((a, b) => a.name.localeCompare(b.name));
      }
      
      state.filteredItems = filtered;
    },
    clearFilters: (state) => {
      state.filters = {
        category: '',
        priceRange: [0, 100000],
        rating: 0,
        sortBy: 'name',
      };
      state.filteredItems = state.items;
    },
  },
});

export const { setProducts, setLoading, setError, setFilters, clearFilters } = productsSlice.actions;
export default productsSlice.reducer;
